import { useState, useMemo } from 'react'
import { Box, Flex, Grid, VStack } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { themeColors } from '../../theme'
import KpiGrid from './components/KpiGrid'
import PeriodSelector from './components/PeriodSelector'
import ChartCard from './components/ChartCard'
import TonnageChart from './components/TonnageChart'
import MineralBreakdownChart from './components/MineralBreakdownChart'
import SupplierRankingChart from './components/SupplierRankingChart'
import RoutePerformanceChart from './components/RoutePerformanceChart'
import DestinationFlowChart from './components/DestinationFlowChart'
import { getDataByPeriod } from '../../data/mockEstadisticas'
import type { PeriodFilter } from '../../data/mockEstadisticas'
import { T, useT } from '../../i18n'

const MotionBox = motion.create(Box)

export default function EstadisticasPage() {
  const colors = themeColors
  const { t } = useT()
  const [period, setPeriod] = useState<PeriodFilter>('12m')

  const data = useMemo(() => getDataByPeriod(period), [period])

  const periodLabel = t(`estadisticas.periods.${period}`)

  return (
    <VStack gap={8} alignItems="stretch">
      {/* Header */}
      <MotionBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={4}>
          <Box>
            <T tid="estadisticas.title" fontSize="2xl" fontWeight="bold" color={colors.text} />
            <T fontSize="10px" textTransform="uppercase" letterSpacing="0.2em" fontWeight="bold" color={colors.textMuted} mt={1}>
              {t('estadisticas.dashboardTitle')}{periodLabel}
            </T>
          </Box>
          <PeriodSelector selected={period} onChange={setPeriod} />
        </Flex>
      </MotionBox>

      {/* KPIs */}
      <KpiGrid data={data.kpis} />

      {/* Row: Tonnage + Mineral Breakdown */}
      <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={6}>
        <ChartCard title={t('estadisticas.charts.tonnageTitle')} subtitle={t('estadisticas.charts.tonnageSubtitle')} detailPath="/app/estadisticas/minerales" delay={0.2}>
          <TonnageChart data={data.monthlyTonnage} />
        </ChartCard>
        <ChartCard title={t('estadisticas.charts.mineralTitle')} subtitle={t('estadisticas.charts.mineralSubtitle')} detailPath="/app/estadisticas/minerales" delay={0.25}>
          <MineralBreakdownChart data={data.mineralBreakdown} />
        </ChartCard>
      </Grid>

      {/* Full-width: Supplier Ranking */}
      <ChartCard title={t('estadisticas.charts.supplierTitle')} subtitle={t('estadisticas.charts.supplierSubtitle')} detailPath="/app/estadisticas/proveedores" delay={0.3}>
        <SupplierRankingChart data={data.topSuppliers} />
      </ChartCard>

      {/* Row: Route Performance + Destination Flow */}
      <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={6}>
        <ChartCard title={t('estadisticas.charts.routeTitle')} subtitle={t('estadisticas.charts.routeSubtitle')} detailPath="/app/estadisticas/rutas" delay={0.35}>
          <RoutePerformanceChart data={data.routeAnalytics} />
        </ChartCard>
        <ChartCard title={t('estadisticas.charts.flowTitle')} subtitle={t('estadisticas.charts.flowSubtitle')} detailPath="/app/estadisticas/transportistas" delay={0.4}>
          <DestinationFlowChart data={data.destinationFlows} />
        </ChartCard>
      </Grid>
    </VStack>
  )
}
